import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { DropdownItem, DropdownMenu, DropdownToggle, Modal, UncontrolledDropdown } from 'reactstrap';
import { addCompetitionReport, updateCompetitionReport } from 'services/api/competition-analysis';
import { fetchSocialAccountsThunk } from 'slices/auth/thunk';
import { fetchCompetitionAnalysisGroupsThunk, fetchCompetitorsThunk } from 'slices/competitionAnalysis/thunk';
import { AppDispatch, RootState } from 'store';
import Select, { components, MultiValueGenericProps, OptionProps } from 'react-select';
import { getErrorMessage } from 'utils/helpers';
import { CompetitionGroup } from 'types/competition-analysis';
import { markGroupDataExpired } from 'slices/competitionAnalysis/reducer';

type SelectOption = {
    value: string;
    label: string;
    avatar: string;
};


const Option = (props: OptionProps<SelectOption>) => (
    <components.Option {...props}>
        <div className="d-flex align-items-center gap-2">
            <img
                src={props.data.avatar}
                alt=""
                style={{ width: 20, height: 20, borderRadius: '50%' }}
            />
            <span>{props.data.label}</span>
        </div>
    </components.Option>
);

const MultiValueLabel = (props: MultiValueGenericProps<SelectOption>) => (
    <components.MultiValueLabel {...props}>
        <div className="d-flex align-items-center gap-2">
            <img
                src={props.data.avatar}
                alt=""
                style={{ width: 16, height: 16, borderRadius: '50%' }}
            />
            <span>{props.data.label}</span>
        </div>
    </components.MultiValueLabel>
);


const AddCompetitionReportModal: React.FC<{ isOpen: boolean, onClose: () => void, selectedReport?: CompetitionGroup | null }> = ({ isOpen, onClose, selectedReport }) => {
    const [processing, setProcessing] = useState(false);

    const dispatch = useDispatch<AppDispatch>();
    const [errors, setErrors] = useState<{ name?: string, selectedSocialAccounts?: string }>({});
    const competitors = useSelector((state: RootState) => state.competitionAnalysis.competitors.items);
    const navigate = useNavigate();

    const [data, setData] = useState<{ name: string, social_account_ids: string[] }>({ name: '', social_account_ids: [] });

    useEffect(() => {
        if (isOpen) {
            if(selectedReport){
                setData({name: selectedReport.name, social_account_ids: selectedReport.accounts.map((a) => a.social_account.uuid)});
            }else{
                setData({ name: '', social_account_ids: [] })
            }
        }
    }, [isOpen, selectedReport])

    const competitorOptions: SelectOption[] = useMemo(() => {
        return competitors.map((c) => ({
            value: c.social_account.uuid,
            label: c.social_account?.name || `@${c.social_account.handle}`,
            avatar: c.social_account.profile_picture_url
        }));
    }, [competitors]);


    const validateForm = () => {
        let newErrors: { name?: string, selectedSocialAccounts?: string } = {};
        if (!data.name) {
            newErrors.name = "Please enter valid report name";
        }
        if (data.social_account_ids.length < 1) {
            newErrors.selectedSocialAccounts = "Please select atleast one competitor.";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }

    const handleSubmit = async (e: any) => {
        e.preventDefault();
        if (!validateForm() || !selectedAgencyId) return;
        try {
            setProcessing(true);
            if(selectedReport){
                await updateCompetitionReport(selectedAgencyId, selectedReport.uuid, data)
            }else {
                await addCompetitionReport(selectedAgencyId, data)
            }
            toast.success(`Competition report ${selectedReport ? 'updated' : 'created'} successfully.`);
            onClose();
            dispatch(fetchCompetitionAnalysisGroupsThunk({ force: true }));
            if(selectedReport){
                dispatch(markGroupDataExpired(selectedReport.uuid));
            }
        } catch (error: any) {
            console.error(error);
            toast.error(getErrorMessage(error) || `Failed to ${selectedReport ? 'update' : 'create'} report. Please try again.`);
        } finally {
            setProcessing(false);
        }
    }

    useEffect(() => {
        if (isOpen) {
            dispatch(fetchCompetitorsThunk({ force: false }));
        }
    }, [isOpen])

    const selectedAgencyId = useSelector((state: RootState) => state.auth.selectedAgencyId);
    return (
        <Modal isOpen={isOpen} toggle={onClose} centered className='application-cost-approval-modal' >
            <div className="modal-body">
                <h6 className='application-cost-approval-modal-title mb-3'>{selectedReport ? 'Update' : 'Create'} Report</h6>
                <div className="mt-4 d-flex flex-column gap-3">
                    <div className="form-group">
                        <div className="d-flex flex-column">
                            <label htmlFor="campaignGoal">Name <span className="text-danger">*</span></label>
                        </div>
                        <input value={data.name}
                            onBlur={validateForm}
                            onChange={(e) => {
                                setData({ ...data, name: e.target.value });
                            }} type="text" className="form-control profile-input-form-control" id="name" name="name" placeholder="Enter report name" />
                        {errors.name && <div className="text-danger font-12 mt-1">{errors.name}</div>}
                    </div>
                    <div className="form-group">
                        <label>
                            Competitors <span className='text-muted'>(Select multiple)</span> <span className="text-danger">*</span>
                        </label>

                        <Select
                            isMulti
                            options={competitorOptions}
                            components={{ Option, MultiValueLabel }}
                            placeholder="Select competitors"
                            value={competitorOptions.filter(opt =>
                                data.social_account_ids.includes(opt.value)
                            )}
                            classNamePrefix={'competitor-select'}
                            onChange={(selected) => {
                                setData({
                                    ...data,
                                    social_account_ids: selected.map(opt => opt.value)
                                });
                            }}
                            styles={{
                                control: (base) => ({
                                    ...base,
                                    borderRadius: 12,
                                    borderColor: '#e5e7eb',
                                    minHeight: 44
                                }),
                                multiValue: (base) => ({
                                    ...base,
                                    borderRadius: 8
                                })
                            }}
                        />

                        {errors.selectedSocialAccounts && (
                            <div className="text-danger font-12 mt-1">
                                {errors.selectedSocialAccounts}
                            </div>
                        )}
                    </div>

                </div>

                {/* Save Changes Button */}
                <div className="mt-4 d-flex justify-content-between align-items-center gap-4">
                    <button className="btn btn-gradient-outline w-50" onClick={onClose}>Cancel</button>
                    <button onClick={handleSubmit} className="btn btn-gradient w-50" disabled={processing || ((data.social_account_ids.length === 0) || !data.name)}>{processing ? 'Please wait...' : selectedReport ? 'Update' : 'Create'}</button>
                </div>
            </div>
        </Modal >
    )
}

export default AddCompetitionReportModal
